// CRÍTICO (founder 21-oct-2026) — «Asignar cliente» a una cita: busca en MIS clientes (nombre, teléfono o correo),
// elige una ficha existente o crea una nueva (teléfono o correo obligatorio, con aviso de nombre repetido).
// El servidor liga la cita a la ficha Y a la cuenta (si la ficha está conectada o su contacto coincide con una cuenta).
import { useEffect, useState } from 'react';
import { Search, UserPlus, X, BadgeCheck, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { clientsApi } from '@/lib/clientsApi';
import { bookingApi } from '@/lib/bookingApi';
import { friendlyError } from '@/lib/apiError';
import { tr } from '@/contexts/LanguageContext';
import { DuplicateAskDialog, isNameDuplicateError } from '@/components/Clients/DuplicateAskDialog';

export const AssignClientPicker = ({ bookingId, onAssigned, onClose }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [nc, setNc] = useState({ name: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [dupAsk, setDupAsk] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => clientsApi.list({ q: q.trim(), limit: 8 }).then((r) => setRows(r?.items || [])).catch(() => setRows([])), 200);
    return () => clearTimeout(t);
  }, [q]);

  const assign = async (body) => {
    setBusy(true);
    try {
      const r = await bookingApi.assignClient(bookingId, body);
      setDupAsk(null);
      toast.success(r.linked_account ? tr("Cita asignada: ya le aparece en su app") : tr("Cita asignada a la ficha (sin cuenta en la app)"));
      onAssigned?.(r.booking);
    } catch (e) {
      if (isNameDuplicateError(e)) { setDupAsk({ duplicates: e.payload.detail.duplicates || [], body }); return; }
      toast.error(friendlyError(e, tr("No pudimos asignar el cliente")));
    } finally { setBusy(false); }
  };

  const createNew = (decision = {}) => {
    if (nc.name.trim().length < 2) { toast.error(tr("Escribe el nombre del cliente")); return; }
    if (!nc.phone.trim() && !nc.email.trim()) { toast.error(tr("Indica teléfono o correo del cliente (al menos uno)")); return; }
    return assign({ name: nc.name.trim(), phone: nc.phone.trim() || undefined, email: nc.email.trim() || undefined, ...decision });
  };

  return (
    <div className="space-y-2" data-testid="assign-client-picker">
      <DuplicateAskDialog open={Boolean(dupAsk)} duplicates={dupAsk?.duplicates || []} busy={busy}
        onSame={(c) => assign({ ...dupAsk.body, merge_into: c.id })} onDifferent={() => assign({ ...dupAsk.body, force_new: true })} onCancel={() => setDupAsk(null)} />
      {!creating ? (
        <>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Nombre, teléfono o correo…")} className="pl-8 bg-background border-border" data-testid="assign-client-search" />
            </div>
            <Button size="icon" variant="ghost" onClick={onClose} data-testid="assign-client-close"><X className="w-4 h-4" /></Button>
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1">
            {rows.map((c) => (
              <button key={c.id} type="button" disabled={busy} onClick={() => assign({ client_record_id: c.id })}
                className="w-full text-left rounded-lg border border-border bg-background px-3 py-2 text-sm hover:border-primary/50 flex items-center gap-2" data-testid={`assign-client-opt-${c.id}`}>
                <span className="flex-1 min-w-0">
                  <span className="font-semibold">{c.display}</span>
                  <span className="block text-[11px] text-muted-foreground truncate">{[c.phone_display || c.phone, c.email].filter(Boolean).join(' · ') || tr("sin contacto")}</span>
                </span>
                {c.user_id && <span className="text-[10px] text-emerald-500 flex items-center gap-0.5 shrink-0"><BadgeCheck className="w-3 h-3" />{tr("con cuenta")}</span>}
              </button>
            ))}
            {!rows.length && q.trim() && <p className="text-[11px] text-muted-foreground px-1" data-testid="assign-client-empty">{tr("Sin resultados en tus clientes.")}</p>}
          </div>
          <Button size="sm" variant="ghost" className="h-8 rounded-full text-primary gap-1" onClick={() => { setCreating(true); setNc({ name: q.trim(), phone: '', email: '' }); }} data-testid="assign-client-new">
            <UserPlus className="w-3.5 h-3.5" />{tr("Nuevo cliente")}
          </Button>
        </>
      ) : (
        <div className="space-y-2" data-testid="assign-client-new-form">
          <p className="text-[11px] text-muted-foreground">{tr("* Teléfono o correo: al menos uno es obligatorio para guardar la ficha.")}</p>
          <Input value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} placeholder={tr("Nombre")} className="bg-background border-border" data-testid="assign-client-new-name" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} inputMode="tel" placeholder={tr("Teléfono (o correo) *")} className="bg-background border-border" data-testid="assign-client-new-phone" />
            <Input value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} inputMode="email" placeholder={tr("Correo (o teléfono) *")} className="bg-background border-border" data-testid="assign-client-new-email" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => setCreating(false)} data-testid="assign-client-new-back">{tr("Volver")}</Button>
            <Button size="sm" className="rounded-full flex-1" disabled={busy} onClick={() => createNew()} data-testid="assign-client-new-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : tr("Asignar")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignClientPicker;
